import { createRef, forwardRef, useRef } from "react";
import { Share2, User } from "lucide-react";
import useTranslation from "../../client/hooks/useTranslation";
import { cn } from "../../client/utils";
import { AnimatedBeam } from "./ui/animated-beam";

/**
 * "Integrations" bento card (Magic UI Animated Beam style) for the landing
 * video section. Three columns — the trader (left), Edgely (center hub) and the
 * supported prop firms (right) — connected by animated beams that flow
 * left → center → each prop firm.
 *
 * Dark-locked like the rest of the landing: logos sit on a light surface so
 * every mark stays legible. Texts come from i18n (t.landing.examples).
 */

// Prop firm display name → logo file under public/logos/propfirms/.
// `dark` flags logos that are white/transparent and need a dark chip to stay visible.
// `bleed` flags square logos that carry their own background: they fill the
// whole circle instead of sitting as a dark square inside a white one.
const PROP_FIRMS: { name: string; src: string; dark?: boolean; bleed?: boolean }[] = [
  { name: "FTMO", src: "/logos/propfirms/ftmo.jpg", bleed: true },
  { name: "Apex", src: "/logos/propfirms/apex.png" },
  { name: "TopStep", src: "/logos/propfirms/topstep.png" },
  { name: "Lucid Trading", src: "/logos/propfirms/lucid.png" },
  { name: "FundedNext", src: "/logos/propfirms/fundednext.png" },
  { name: "MyFundedFutures", src: "/logos/propfirms/myfundedfutures.png" },
  { name: "TakeProfitTrader", src: "/logos/take-profit-trader-logo.webp" },
  { name: "Tradeify", src: "/logos/propfirms/tradeify.png" },
  { name: "Phidias", src: "/logos/propfirms/phidias.jpg", bleed: true },
  { name: "Funded Futures Family", src: "/logos/propfirms/fff.png" },
];

// Circular node holding a logo / icon. Light surface keeps every mark legible.
const Node = forwardRef<
  HTMLDivElement,
  {
    className?: string;
    children: React.ReactNode;
    size?: "sm" | "md" | "lg";
    dark?: boolean;
    bleed?: boolean;
  }
>(({ className, children, size = "md", dark = false, bleed = false }, ref) => {
  const sizes = {
    sm: "h-9 w-9 sm:h-10 sm:w-10",
    md: "h-12 w-12 sm:h-14 sm:w-14",
    lg: "h-14 w-14 sm:h-16 sm:w-16",
  };
  return (
    <div
      ref={ref}
      className={cn(
        "z-10 flex shrink-0 items-center justify-center rounded-full shadow-[0_0_20px_-12px_rgba(0,0,0,0.8)]",
        bleed ? "overflow-hidden p-0" : "p-2",
        dark ? "bg-neutral-900 ring-1 ring-white/10" : "bg-white ring-1 ring-black/5",
        sizes[size],
        className,
      )}
    >
      {children}
    </div>
  );
});
Node.displayName = "Node";

export default function IntegrationsCard({
  showFooter = true,
}: {
  showFooter?: boolean;
}) {
  const t = useTranslation();

  const containerRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);
  const edgelyRef = useRef<HTMLDivElement>(null);
  // Stable ref per prop firm so AnimatedBeam reads a populated `.current`
  // after mount (a fresh `{ current }` literal would capture null).
  const firmRefs = useRef(
    PROP_FIRMS.map(() => createRef<HTMLDivElement>()),
  );

  return (
    <div className="mx-auto mt-10 max-w-3xl">
      <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-card p-6 shadow-xl shadow-primary/5 sm:p-8">
        {/* Beams layout */}
        <div
          ref={containerRef}
          className="relative flex h-[420px] w-full items-stretch justify-between sm:h-[480px]"
        >
          {/* Col 1 — the trader */}
          <div className="flex flex-col justify-center">
            <Node ref={userRef} size="md">
              <User className="h-6 w-6 text-neutral-700" />
            </Node>
          </div>

          {/* Col 2 — Edgely hub */}
          <div className="flex flex-col justify-center">
            <Node ref={edgelyRef} size="lg">
              <img
                src="/logos/edgely-logo-light.png"
                alt="Edgely"
                className="h-full w-full object-contain"
                loading="lazy"
              />
            </Node>
          </div>

          {/* Col 3 — prop firms */}
          <div className="flex flex-col justify-between py-1">
            {PROP_FIRMS.map((firm, i) => (
              <Node
                key={firm.name}
                size="sm"
                dark={firm.dark}
                bleed={firm.bleed}
                ref={firmRefs.current[i]}
              >
                <img
                  src={firm.src}
                  alt={`${firm.name} logo`}
                  className={cn(
                    "h-full w-full",
                    firm.bleed ? "object-cover" : "object-contain",
                  )}
                  loading="lazy"
                />
              </Node>
            ))}
          </div>

          {/* Beam: trader → Edgely */}
          <AnimatedBeam
            containerRef={containerRef}
            fromRef={userRef}
            toRef={edgelyRef}
            pathColor="#64748b"
            pathOpacity={0.25}
            duration={4}
          />

          {/* Beams: Edgely → each prop firm */}
          {PROP_FIRMS.map((firm, i) => (
            <AnimatedBeam
              key={firm.name}
              containerRef={containerRef}
              fromRef={edgelyRef}
              toRef={firmRefs.current[i]}
              curvature={(i - (PROP_FIRMS.length - 1) / 2) * 14}
              pathColor="#64748b"
              pathOpacity={0.25}
              duration={4}
              delay={i * 0.25}
            />
          ))}
        </div>

        {/* Footer text */}
        {showFooter && (
          <div className="mt-6 flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Share2 className="h-5 w-5" />
            </span>
            <div>
              <h3 className="text-lg font-bold text-foreground">
                {t.landing.examples.integrationsTitle}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {t.landing.examples.integrationsSubtitle}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
